import React, { useState, useEffect } from 'react';
import './HabitTracker.css';
import { getPersonalizedHabits } from '../data/habitDefinitions';
import {
  initializeHabitSchedules,
  getHabitsForDate,
  completeHabit,
  calculateDayCompletion,
  getCompletionStreak,
  getStartOfDay,
  getUpcomingHabits,
  getScheduleSummary
} from '../utils/habitScheduler';
import { 
  generateWelcomeMessage, 
  getDailyTips, 
  getMotivationalMessage 
} from '../utils/personalization';
import DateNavigation from './DateNavigation';
import HabitList from './HabitList';
import ProgressCard from './ProgressCard';
import Header from './Header';
import NotificationToast from './NotificationToast';
import WeeklyOverview from './WeeklyOverview';
import HabitInsights from './HabitInsights';
import HabitSchedulePreview from './HabitSchedulePreview';
import { createConfettiEffect, celebrateAchievement } from '../utils/feedback';

const HabitTracker = ({ userProfile, onResetProfile }) => {
  const [currentDate, setCurrentDate] = useState(getStartOfDay());
  const [personalizedHabits, setPersonalizedHabits] = useState([]);
  const [habitSchedules, setHabitSchedules] = useState({});
  const [todaysHabits, setTodaysHabits] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [welcomeMessage, setWelcomeMessage] = useState(null);
  const [dailyTips, setDailyTips] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [previousCompletion, setPreviousCompletion] = useState(0);
  const [showSchedulePreview, setShowSchedulePreview] = useState(false);

  // Initialize habits and schedules
  useEffect(() => {
    const initializeUserHabits = () => {
      // Get personalized habits based on user profile
      const habits = getPersonalizedHabits(userProfile);
      setPersonalizedHabits(habits);

      // Load existing schedules or initialize new ones
      const savedData = localStorage.getItem('prettyHabitsData');
      let schedules;
      
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        schedules = parsedData.habitSchedules || {};
        
        // Convert date strings back to Date objects
        Object.keys(schedules).forEach(habitId => {
          if (schedules[habitId].nextOccurrence) {
            schedules[habitId].nextOccurrence = new Date(schedules[habitId].nextOccurrence);
          }
          if (schedules[habitId].lastCompleted) {
            schedules[habitId].lastCompleted = new Date(schedules[habitId].lastCompleted);
          }
        });
      } else {
        schedules = initializeHabitSchedules(habits);
      }
      
      setHabitSchedules(schedules);
      
      // Generate welcome message and tips
      setWelcomeMessage(generateWelcomeMessage(userProfile));
      setDailyTips(getDailyTips(userProfile, habits));
      
      setIsLoading(false);
    };

    initializeUserHabits();
  }, [userProfile]);

  // Update today's habits when date or schedules change
  useEffect(() => {
    if (personalizedHabits.length > 0 && Object.keys(habitSchedules).length > 0) {
      const habitsForToday = getHabitsForDate(personalizedHabits, habitSchedules, currentDate);
      setTodaysHabits(habitsForToday);

      // Update previous completion for confetti tracking
      const completion = calculateDayCompletion(habitsForToday);
      setPreviousCompletion(completion);
    }
  }, [currentDate, personalizedHabits, habitSchedules]);

  // Save data to localStorage whenever schedules change
  useEffect(() => {
    if (Object.keys(habitSchedules).length > 0) {
      const dataToSave = {
        habitSchedules,
        lastUpdated: new Date().toISOString()
      };
      localStorage.setItem('prettyHabitsData', JSON.stringify(dataToSave));
    }
  }, [habitSchedules]);

  const handleHabitComplete = (habitId) => {
    const updatedSchedules = completeHabit(habitId, currentDate, habitSchedules, personalizedHabits);
    setHabitSchedules(updatedSchedules);

    // Calculate new completion percentage
    const updatedHabits = getHabitsForDate(personalizedHabits, updatedSchedules, currentDate);
    const newCompletion = calculateDayCompletion(updatedHabits);

    // Show completion notification
    const habit = personalizedHabits.find(h => h.id === habitId);
    if (habit) {
      const newNotification = {
        id: Date.now(),
        message: `Great job! You completed "${habit.name}"`,
        type: 'success'
      };
      setNotifications(prev => [...prev, newNotification]);
    }

    // Celebrate 100% completion with confetti
    if (newCompletion === 100 && previousCompletion < 100) {
      setTimeout(() => {
        // Find the progress card element and trigger confetti
        const progressCard = document.querySelector('.progress-card');
        if (progressCard) {
          createConfettiEffect(progressCard);
          celebrateAchievement(progressCard, 'perfect-day');
        }

        // Add celebration notification
        const celebrationNotification = {
          id: Date.now() + 1,
          message: '🎉 Perfect day! You completed all your habits!',
          type: 'celebration'
        };
        setNotifications(prev => [...prev, celebrationNotification]);
      }, 500); // Small delay to let the UI update
    }

    setPreviousCompletion(newCompletion);
  };

  const handleDateChange = (newDate) => {
    setCurrentDate(getStartOfDay(newDate));
  };

  const removeNotification = (notificationId) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  };

  const handleProfileUpdate = (updatedProfile) => {
    localStorage.setItem('prettyHabitsProfile', JSON.stringify(updatedProfile));
    // Re-initialize habits with new profile
    const habits = getPersonalizedHabits(updatedProfile);
    setPersonalizedHabits(habits);
    setWelcomeMessage(generateWelcomeMessage(updatedProfile));
    setDailyTips(getDailyTips(updatedProfile, habits));
  };

  const calculateStats = () => {
    const dayCompletion = calculateDayCompletion(todaysHabits);
    const streak = getCompletionStreak(habitSchedules, personalizedHabits, currentDate);
    const motivationalMsg = getMotivationalMessage(streak, dayCompletion);

    return { dayCompletion, streak, motivationalMsg };
  };

  // Get schedule information for preview
  const scheduleSummary = getScheduleSummary(personalizedHabits, habitSchedules);
  const upcomingHabits = getUpcomingHabits(personalizedHabits, habitSchedules, 7);

  if (isLoading) {
    return (
      <div className="habit-tracker-loading">
        <div className="loading-spinner"></div>
        <p>Preparing your personalized routine...</p>
      </div>
    );
  }

  const stats = calculateStats();

  return (
    <div className="habit-tracker">
      <Header
        userProfile={userProfile}
        welcomeMessage={welcomeMessage}
        onResetProfile={onResetProfile}
        onUpdateProfile={handleProfileUpdate}
      />
      
      <div className="habit-tracker-content">
        <DateNavigation 
          currentDate={currentDate}
          onDateChange={handleDateChange}
        />
        
        <ProgressCard
          dayCompletion={stats.dayCompletion}
          streak={stats.streak}
          motivationalMessage={stats.motivationalMsg}
          dailyTips={dailyTips}
        />

        {/* Schedule Preview - Show on first few days or when toggled */}
        {(stats.streak < 3 || showSchedulePreview) && (
          <div className="schedule-preview-container">
            <div className="schedule-toggle">
              <button
                className="schedule-toggle-btn"
                onClick={() => setShowSchedulePreview(!showSchedulePreview)}
              >
                {showSchedulePreview ? '📅 Hide Schedule' : '📅 View Your Schedule'}
              </button>
            </div>
            {showSchedulePreview && (
              <HabitSchedulePreview
                scheduleSummary={scheduleSummary}
                upcomingHabits={upcomingHabits}
              />
            )}
          </div>
        )}

        <WeeklyOverview
          personalizedHabits={personalizedHabits}
          habitSchedules={habitSchedules}
          currentDate={currentDate}
        />

        <HabitInsights
          userProfile={userProfile}
          habitSchedules={habitSchedules}
          personalizedHabits={personalizedHabits}
        />
        
        <HabitList
          habits={todaysHabits}
          onHabitComplete={handleHabitComplete}
          currentDate={currentDate}
        />
      </div>

      {/* Notifications */}
      {notifications.map(notification => (
        <NotificationToast
          key={notification.id}
          message={notification.message}
          type={notification.type}
          onClose={() => removeNotification(notification.id)}
        />
      ))}
    </div>
  );
};

export default HabitTracker;
